import { Controller } from "@hotwired/stimulus"

// Creating a food without losing where you were.
//
// A recipe half-entered is easy to lose, and so is a meal slot: sending
// someone to /foods/new and back drops either. So the form posts in the
// background and hands the food to whatever asked for it.
//
// WHY A TOKEN AND NOT A REFERENCE. The old dialog was opened by a JS event
// carrying the combobox object itself. This modal is server-rendered into
// the shared frame like every other modal in the app, so the request cannot
// carry an object — the combobox stamps a token on its own element, and
// this looks the element back up when there is a food to hand over.
//
// The token can be absent: opened from the meal picker there is no combobox
// to hand anything to, and the server redirects to the amount step instead.
export default class extends Controller {
  static targets = ["form", "error"]
  // slot: where the picker was, when it was the picker that opened this.
  // Its presence is what says "there is no row to adopt into" — see hand().
  // host: the page the picker was opened over — the planner or today.
  // slot: the picker's own URL, which is layered onto that page.
  static values = { token: String, slot: String, host: String }

  async submit(event) {
    event.preventDefault()

    const response = await fetch(this.formTarget.action, {
      method: "POST",
      body: new FormData(this.formTarget),
      headers: { Accept: "application/json" }
    })

    if (!response.ok) {
      const payload = await response.json().catch(() => ({}))
      this.errorTarget.textContent =
        Object.values(payload.errors || {}).flat().join(". ") ||
        "That food could not be saved."
      this.errorTarget.hidden = false
      // THE PANEL IS ABOVE THE STEPS and you are on the last one when you
      // submit, so an error could appear entirely off screen — the form
      // simply did nothing, which is how "it also didn't create it (maybe
      // rejected?)" happens.
      this.errorTarget.scrollIntoView({ block: "nearest", behavior: "smooth" })
      return
    }

    this.hand(await response.json())
  }

  // A DUPLICATE CHOSEN IS A CREATE ABANDONED, and it ends where a successful
  // create does. This is the point of showing matches at all: search ranks
  // plain chicken breast 34th behind giblets, so "it is already here and I
  // cannot find it" is commoner than "it is missing".
  chosen(event) {
    const food = event.detail.food

    // A duplicate chosen from the PICKER goes to that food's amount step,
    // exactly as a newly created one does. It comes from the matcher rather
    // than from create, so there is no redirect on it and the destination
    // is built here from the slot the modal was opened with.
    if (this.hasSlotValue && this.slotValue) {
      // TO THE HOST, carrying the picker — never to the picker itself. It
      // is layered onto a page and answers a direct visit with a bare
      // frame, so navigating there gives an unstyled fragment and anything
      // done from it has nothing to render into.
      const picker = new URL(this.slotValue, window.location.origin)
      picker.searchParams.set("preview_food", food.id)

      const host = new URL(this.hostValue || this.slotValue, window.location.origin)
      host.searchParams.set("open_picker", picker.pathname + picker.search)

      return this.leave(host.toString())
    }

    this.hand(food)
  }

  hand(food) {
    // THE TWO CALLERS END DIFFERENTLY, and deliberately so. The recipe
    // editor writes the food into the row that asked for it; the picker has
    // no row and goes to the new food's amount step, because the food now
    // exists but HOW MUCH has not been answered — 1 slice and 4 slices are
    // different lunches.
    //
    // The server decides, because only one of them sent a slot.
    // A FULL VISIT, not a frame navigation. The picker renders its modal
    // from the PAGE; on a frame request it returns only its inner
    // #meal_picker_results. Navigating the modal frame to it therefore
    // found no matching frame and Turbo wrote "Content missing" where the
    // panel had been.
    if (food.redirect_to) return this.leave(food.redirect_to)

    this.requester?.adopt({
      id: food.id,
      name: food.name,
      units: food.units || [ "g" ],
      mention_terms: food.mention_terms,
      own_mention_terms: food.own_mention_terms
    })

    this.close()
  }

  // LEAVING THE FRAME, WHICH Turbo.visit CANNOT DO.
  //
  // This controller lives inside <turbo-frame id="modal">, and a visit from
  // within a frame is scoped to it: Turbo fetched /today, looked for a
  // #modal in the response, found none, and wrote "Content missing" at the
  // bottom of the page without navigating.
  //
  // `_top` is how the rest of the app escapes a frame — see foods/index and
  // the shopping list — and a link is the only place Turbo reads it, so
  // this is a click rather than an API call.
  leave(url) {
    const link = document.createElement("a")
    link.href = url
    link.dataset.turboFrame = "_top"
    link.hidden = true
    document.body.append(link)
    link.click()
    link.remove()
  }

  // The combobox that asked, found by the token it stamped on itself. Looked
  // up at hand-over time rather than on connect: the modal renders into a
  // frame, and the page underneath is not this controller's to assume
  // anything about until it is needed.
  get requester() {
    if (!this.hasTokenValue || !this.tokenValue) return null

    const element = document.querySelector(
      `[data-combobox-token="${CSS.escape(this.tokenValue)}"]`
    )
    if (!element) return null

    return this.application.getControllerForElementAndIdentifier(element, "combobox")
  }

  close() {
    this.application
      .getControllerForElementAndIdentifier(this.element, "modal")
      ?.close()
  }
}
